import React, { useState } from 'react';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';

interface ActionItem {
  id: string;
  title: string;
  functionName: string;
  owner: string;
  dueDate: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Overdue' | 'In Progress' | 'Completed';
}

const INITIAL_ACTIONS: ActionItem[] = [
  {
    id: 'ACT-01',
    title: 'Configure warm backup server for payroll processing',
    functionName: 'Payroll processing',
    owner: 'A. Okafor',
    dueDate: 'Oct 02, 2026',
    priority: 'High',
    status: 'Overdue',
  },
  {
    id: 'ACT-02',
    title: 'Acquire secondary VoIP SIP trunk provider to eliminate single point of failure',
    functionName: 'Customer support line',
    owner: 'J. Tremblay',
    dueDate: 'Oct 05, 2026',
    priority: 'High',
    status: 'Overdue',
  },
  {
    id: 'ACT-03',
    title: 'Conduct tabletop drill for ransomware encryption scenario',
    functionName: 'IT & Security',
    owner: 'K. Lavoie',
    dueDate: 'Oct 10, 2026',
    priority: 'High',
    status: 'Overdue',
  },
  {
    id: 'ACT-04',
    title: 'Review and sign updated SLA contract with primary logistics carrier',
    functionName: 'Order fulfilment',
    owner: 'M. Singh',
    dueDate: 'Oct 18, 2026',
    priority: 'Medium',
    status: 'In Progress',
  },
  {
    id: 'ACT-05',
    title: 'Document offline paper workflow for clinical record intake during network blackouts',
    functionName: 'Patient records access',
    owner: 'R. Bello',
    dueDate: 'Oct 22, 2026',
    priority: 'High',
    status: 'In Progress',
  },
  {
    id: 'ACT-06',
    title: 'Validate emergency dual-authorization protocol for wire disbursements',
    functionName: 'Vendor payments',
    owner: 'A. Okafor',
    dueDate: 'Oct 25, 2026',
    priority: 'Medium',
    status: 'In Progress',
  },
  {
    id: 'ACT-07',
    title: 'Update contact directory and alternate crisis channels in WhatsApp/Signal',
    functionName: 'Crisis Management',
    owner: 'J. Tremblay',
    dueDate: 'Oct 29, 2026',
    priority: 'Medium',
    status: 'In Progress',
  },
  {
    id: 'ACT-08',
    title: 'Schedule annual generator load testing for regional distribution warehouse',
    functionName: 'Facilities',
    owner: 'M. Singh',
    dueDate: 'Nov 02, 2026',
    priority: 'Low',
    status: 'In Progress',
  },
  {
    id: 'ACT-09',
    title: 'Enable MFA hardware security keys for all ERP system administrators',
    functionName: 'IT Security',
    owner: 'K. Lavoie',
    dueDate: 'Nov 05, 2026',
    priority: 'High',
    status: 'In Progress',
  },
  {
    id: 'ACT-10',
    title: 'Re-verify automated hourly snapshot replication to secondary AWS region',
    functionName: 'Infrastructure',
    owner: 'K. Lavoie',
    dueDate: 'Nov 12, 2026',
    priority: 'Medium',
    status: 'In Progress',
  },
  {
    id: 'ACT-11',
    title: 'Distribute physical wallet emergency instruction cards to incident commanders',
    functionName: 'Executive Team',
    owner: 'R. Bello',
    dueDate: 'Nov 15, 2026',
    priority: 'Low',
    status: 'In Progress',
  },
  {
    id: 'ACT-12',
    title: 'Complete annual ISO 22301 Clause 4 context review',
    functionName: 'Compliance',
    owner: 'R. Bello',
    dueDate: 'Sep 15, 2026',
    priority: 'Medium',
    status: 'Completed',
  },
  {
    id: 'ACT-13',
    title: 'Audit critical third-party vendor SOC 2 reports',
    functionName: 'Risk & Audit',
    owner: 'A. Okafor',
    dueDate: 'Sep 20, 2026',
    priority: 'Medium',
    status: 'Completed',
  },
  {
    id: 'ACT-14',
    title: 'Test automated mass notification SMS broadcast system',
    functionName: 'Crisis Communications',
    owner: 'J. Tremblay',
    dueDate: 'Sep 28, 2026',
    priority: 'High',
    status: 'Completed',
  },
];

export const ActionsPage: React.FC = () => {
  const [actions, setActions] = useState<ActionItem[]>(INITIAL_ACTIONS);
  const [filterTab, setFilterTab] = useState<'all' | 'overdue' | 'in_progress' | 'completed'>('all');

  const toggleComplete = (id: string) => {
    setActions((prev) =>
      prev.map((act) => {
        if (act.id === id) {
          return {
            ...act,
            status: act.status === 'Completed' ? 'In Progress' : 'Completed',
          };
        }
        return act;
      })
    );
  };

  const overdueCount = actions.filter((a) => a.status === 'Overdue').length;
  const inProgressCount = actions.filter((a) => a.status === 'In Progress').length;
  const completedCount = actions.filter((a) => a.status === 'Completed').length;

  const filtered = actions.filter((a) => {
    if (filterTab === 'overdue') return a.status === 'Overdue';
    if (filterTab === 'in_progress') return a.status === 'In Progress';
    if (filterTab === 'completed') return a.status === 'Completed';
    return true;
  });

  return (
    <WorkspaceLayout
      title="Actions & Remediation"
      subtitle="Track gaps, tabletop exercise findings, and ISO 22301 remediation tasks"
    >
      <div className="space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Total Actions
            </div>
            <div className="mt-2 text-3xl font-extrabold text-slate-900">{actions.length}</div>
            <div className="mt-1 text-xs text-slate-500">Across 6 owners</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Overdue
            </div>
            <div className="mt-2 text-3xl font-extrabold text-[#DC2626]">{overdueCount}</div>
            <div className="mt-1 text-xs text-rose-600 font-medium">Requires immediate escalation</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              In Progress
            </div>
            <div className="mt-2 text-3xl font-extrabold text-slate-900">{inProgressCount}</div>
            <div className="mt-1 text-xs text-slate-500">Targeted for completion this month</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Completed
            </div>
            <div className="mt-2 text-3xl font-extrabold text-emerald-600">{completedCount}</div>
            <div className="mt-1 text-xs text-slate-500">Verified in last audit cycle</div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {/* Status Tabs */}
          <div className="border-b border-slate-200 px-6 pt-4 flex space-x-8">
            <button
              type="button"
              onClick={() => setFilterTab('all')}
              className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
                filterTab === 'all'
                  ? 'border-[#991B1B] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>All</span>
              <span className="px-1.5 py-0.2 rounded-full text-[11px] bg-slate-100 text-slate-700">
                {actions.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setFilterTab('overdue')}
              className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
                filterTab === 'overdue'
                  ? 'border-[#991B1B] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>Overdue</span>
              <span className="px-1.5 py-0.2 rounded-full text-[11px] bg-rose-100 text-rose-700">
                {overdueCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setFilterTab('in_progress')}
              className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
                filterTab === 'in_progress'
                  ? 'border-[#991B1B] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>In Progress</span>
              <span className="px-1.5 py-0.2 rounded-full text-[11px] bg-slate-100 text-slate-700">
                {inProgressCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setFilterTab('completed')}
              className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
                filterTab === 'completed'
                  ? 'border-[#991B1B] text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>Completed</span>
              <span className="px-1.5 py-0.2 rounded-full text-[11px] bg-emerald-100 text-emerald-700">
                {completedCount}
              </span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-6 w-10"></th>
                  <th className="py-3 px-4">Action Item</th>
                  <th className="py-3 px-4">Associated Function</th>
                  <th className="py-3 px-4">Owner</th>
                  <th className="py-3 px-4">Due Date</th>
                  <th className="py-3 px-4">Priority</th>
                  <th className="py-3 px-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((item) => {
                  const isDone = item.status === 'Completed';
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors cursor-pointer"
                      onClick={() => toggleComplete(item.id)}
                    >
                      <td className="py-3.5 px-6">
                        <input
                          type="checkbox"
                          checked={isDone}
                          onChange={() => toggleComplete(item.id)}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">
                        <span className={isDone ? 'line-through text-slate-400' : ''}>
                          {item.title}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600">{item.functionName}</td>
                      <td className="py-3.5 px-4 font-medium text-slate-800">{item.owner}</td>
                      <td className="py-3.5 px-4 text-slate-500">{item.dueDate}</td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            item.priority === 'High'
                              ? 'bg-rose-50 text-rose-700'
                              : item.priority === 'Medium'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {item.priority}
                        </span>
                      </td>
                      <td className="py-3.5 px-6 text-right">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                            item.status === 'Overdue'
                              ? 'bg-[#FEE2E2] text-[#991B1B]'
                              : item.status === 'Completed'
                              ? 'bg-[#DCFCE7] text-[#166534]'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default ActionsPage;
